import { useCallback, useMemo, useState } from 'react';
import {
  DataGridPro,
  GridActionsCellItem,
  GridEditInputCell,
  useGridApiRef
} from '@mui/x-data-grid-pro';
import { Alert, Box, Button, Tooltip } from '@mui/material';
import Grid from '@mui/material/Grid';
import MultilineCellEdit from '../MultilineCellEdit';
import {
  getAERatingColumn,
  getPSStatusColumnObject,
  handleDoubleClickEvent,
  psDefaultStatus,
  sbmColumn
} from './PSSupplierDataGridUtils';
import {
  EDITABLE_CURRENT_PS_COLUMNS,
  EDITABLE_NON_CURRENT_PS_COLUMNS,
  SPLR_TYPE_CURRENT,
  SPLR_TYPE_NON_CURRENT
} from './PSAppConstants';
import { supplierDataGridSx } from '../supplierDataGridStyles';
import GetSDIMasterList from './GetSDIMasterList';
import PSSDIEdit from './PSSDIEdit';
import PSNameEdit from './PSNameEdit';
import { PSSupplierDataGridRowDetails } from './PSSupplierDataGridRowDetails';
import { useDispatch, useSelector } from 'react-redux';
import {
  addSupplier,
  deleteSupplier,
  editKeyOrCompany,
  editSupplier,
  setExpandedRowIds
} from './prospectiveSupplierSlice';
import { defaultStatus } from '../SupplierDataGridUtils';
import ProspectiveSupplierDropDownSearch from './ProspectiveSupplierDropDownSearch';
import {
  getSuppliersWithSroSbmPopulated,
  handlePromiseResult,
  populateAERatingBySupplierNumber
} from '../../../shared/UtilFunctions';
import { getSdiMap, getSuppliersSroSbmMap } from '../../../shared/APIHelper';
import {
  AE_RATING_API_ERR_MSG,
  AE_RATING_PROP_NAME,
  SDIS_API_ERR_MSG,
  SDIS_PROP_NAME,
  SRO_SBM_API_ERR_MSG,
  SRO_SBM_PROP_NAME,
  SRO_SBM_USER_ID_PROP_NAME
} from '../../../shared/AppConstants';

export default function ProspectiveSupplierDataGrid(props) {
  const apiRef = useGridApiRef();
  const {
    isEdit,
    updateSupplierComments,
    updateSupplierStatus,
    commonProps,
    noRowsMessage,
    setSuppliersDirty,
    setSuppliersMessage
  } = props;

  const prospectiveSuppliersRows = useSelector(
    (state) => state.prospectiveSupplier.prospectiveSupplierRows
  );
  const addRowEnabled = useSelector(
    (state) => state.prospectiveSupplier.addRowEnabled
  );
  const expandedRowIds = useSelector(
    (state) => state.prospectiveSupplier.expandedRowIds
  );
  const expandedRowIdSet = useMemo(
    () => new Set(expandedRowIds),
    [expandedRowIds]
  );

  const dispatch = useDispatch();
  const [sdiMasterList, setSdiMasterList] = useState();
  const [isDuplicate, setIsDuplicate] = useState(false);

  const renderEditTextarea = (params) => <MultilineCellEdit {...params} />;

  function prepareCurrentProspectiveSupplier(splr) {
    if (splr) {
      return {
        type: splr.type,
        number: splr.supplierNumber,
        name: splr.name,
        status: splr.status,
        comments: splr?.comments?.replaceAll(/(<([^</>]+)>)/gi, ' '),
        city: splr.city,
        region: splr.region,
        country: splr.country,
        sdis: splr.sdis,
        id: splr.id,
        aeRating: splr.aeRating,
        sroSbmName: splr[SRO_SBM_PROP_NAME],
        sroSbmUserId: splr[SRO_SBM_USER_ID_PROP_NAME]
      };
    }
  }

  function getNameViewCell(params) {
    if (params.row.name?.length === 0)
      return params.row.type === SPLR_TYPE_NON_CURRENT ? (
        <PSNameEdit {...params} setterFn={setNonCurrentSelectedSupplier} />
      ) : (
        <ProspectiveSupplierDropDownSearch
          commonProps={commonProps}
          setSelectedProspectiveSuppliers={setSelectedProspectiveSuppliers}
          isDuplicate={isDuplicate}
        />
      );
    else return params.row.name;
  }

  function getNameEditCell(params) {
    if (params.row.type === SPLR_TYPE_NON_CURRENT)
      return (
        <PSNameEdit {...params} setterFn={setNonCurrentSelectedSupplier} />
      );
    else if (
      params.row.type === SPLR_TYPE_CURRENT &&
      params.row.name.length === 0
    )
      return (
        <ProspectiveSupplierDropDownSearch
          commonProps={commonProps}
          setSelectedProspectiveSuppliers={setSelectedProspectiveSuppliers}
          isDuplicate={isDuplicate}
        />
      );
    else return null;
  }

  const splrNameColumn = {
    field: 'name',
    headerName: 'Name',
    width: 300,
    editable: isEdit,
    renderCell: (params) => getNameViewCell(params),
    renderEditCell: (params) => getNameEditCell(params)
  };

  const columns = [
    {
      field: 'id',
      headerName: 'ID',
      type: 'number',
      width: 125
    },
    {
      field: 'number',
      headerName: 'Number',
      width: 125
    },
    splrNameColumn,
    getPSStatusColumnObject(isEdit),
    {
      field: 'sdis',
      headerName: 'SD Indicators',
      width: 130,
      sortable: false,
      editable: isEdit,
      renderEditCell: (params) => (
        <PSSDIEdit {...params} options={sdiMasterList} />
      ),
      valueFormatter: (value) => (value ? value.join(', ') : value)
    },
    getAERatingColumn(),
    sbmColumn,
    {
      field: 'city',
      headerName: 'City',
      width: 130,
      editable: isEdit,
      renderEditCell: (params) => (
        <GridEditInputCell
          {...params}
          inputProps={{
            maxLength: 40
          }}
        />
      )
    },
    {
      field: 'region',
      headerName: 'Region',
      width: 130,
      editable: isEdit,
      renderEditCell: (params) => (
        <GridEditInputCell
          {...params}
          inputProps={{
            maxLength: 40
          }}
        />
      )
    },
    {
      field: 'country',
      headerName: 'Country',
      width: 130,
      editable: isEdit,
      renderEditCell: (params) => (
        <GridEditInputCell
          {...params}
          inputProps={{
            maxLength: 3
          }}
        />
      )
    },
    {
      field: 'comments',
      headerName: 'Comments',
      minWidth: 410,
      flex: 1,
      editable: isEdit,
      resizable: true,
      renderEditCell: renderEditTextarea,
      renderCell: (params) => (
        <Tooltip title={params?.value?.replaceAll(/(<([^</>]+)>)/gi, ' ')}>
          <div
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              width: 410
            }}
          >
            {params.value}
          </div>
        </Tooltip>
      )
    },
    ...(isEdit
      ? [
          {
            field: 'actions',
            type: 'actions',
            headerName: 'Delete',
            minWidth: 75,
            flex: 1,
            cellClassName: 'actions',
            getActions: ({ id }) => {
              return [
                <GridActionsCellItem
                  icon={
                    <Tooltip title={'Delete'}>
                      <fuel-icon name="delete" />
                    </Tooltip>
                  }
                  label={'Delete'}
                  key={id}
                  onClick={handleDeleteClick(id)}
                />
              ];
            }
          }
        ]
      : [])
  ];

  const handleDetailPanelExpandedRowIdsChange = useCallback((newIds) => {
    dispatch(setExpandedRowIds(Array.from(newIds)));
  }, []);

  const setDirty = () => {
    setSuppliersDirty(true);
    setSuppliersMessage &&
      setSuppliersMessage(
        'Changes to supplier will not take effect until strategy is saved.'
      );
  };

  function updateProspectiveSupplier(rowId, obj) {
    dispatch(editKeyOrCompany({ id: rowId, updatedField: obj }));
    setDirty();
  }

  const getDetailPanelContent = useCallback(({ row }) => {
    return (
      <PSSupplierDataGridRowDetails
        rowId={row?.id}
        isEdit={isEdit}
        updateProspectiveSupplier={updateProspectiveSupplier}
      />
    );
  }, []);

  // const getDetailPanelHeight = useCallback(
  //   () => (getEnvVar('VITE_ENV') === 'local' ? 300 : 'auto'),
  //   []
  // );
  const getDetailPanelHeight = useCallback(() => 'auto', []);

  const handleCellClick = useCallback(
    (params, event) => handleDoubleClickEvent(params, event),
    []
  );

  const handleProcessRowUpdateError = useCallback(
    (error) => console.log(error),
    []
  );

  function setNonCurrentSelectedSupplier(splrName, rowId) {
    const splrObj = { ...prospectiveSuppliersRows[rowId], name: splrName };

    dispatch(editSupplier(splrObj));
    setDirty();
  }

  async function setSelectedProspectiveSuppliers(splr) {
    const splrNumberToAdd = splr.value;

    // check duplicates
    const supplierIdList = prospectiveSuppliersRows
      .filter((splr) => splr.number > 0)
      .map((splr) => splr.number);

    if (supplierIdList.length > 0 && supplierIdList.includes(splrNumberToAdd)) {
      setIsDuplicate(true);
    } else {
      const splrName = splr.label.replace(`${splrNumberToAdd} - `, '');
      const currentRowId = prospectiveSuppliersRows.length - 1;
      const splrObj = {
        ...splr,
        supplierNumber: splrNumberToAdd,
        status: defaultStatus,
        comments: '',
        name: splrName,
        id: currentRowId,
        type: SPLR_TYPE_CURRENT
      };

      const promises = [];

      promises.push(populateAERatingBySupplierNumber(splrNumberToAdd));
      promises.push(getSdiMap(splrNumberToAdd));
      promises.push(getSuppliersSroSbmMap(splrNumberToAdd));

      const [aeRating, sdis, sroSbmMap] = await Promise.allSettled(promises);

      handlePromiseResult(
        aeRating,
        AE_RATING_PROP_NAME,
        AE_RATING_API_ERR_MSG,
        splrObj,
        commonProps
      );
      handlePromiseResult(
        sdis,
        SDIS_PROP_NAME,
        SDIS_API_ERR_MSG,
        splrObj,
        commonProps
      );

      if (sroSbmMap.status === 'rejected') {
        commonProps.setStatusErrorMsg(SRO_SBM_API_ERR_MSG);
      } else {
        getSuppliersWithSroSbmPopulated(sroSbmMap.value, [splrObj]);
      }

      const currentProspectiveSupplier =
        prepareCurrentProspectiveSupplier(splrObj);

      dispatch(editSupplier(currentProspectiveSupplier));
      setDirty();
      setTimeout(() => {
        apiRef.current.startCellEditMode({ id: currentRowId, field: 'status' });
      }, [10]);
      setIsDuplicate(false);
    }
  }

  function addNonCurrentSupplier() {
    if (addRowEnabled) {
      let val;

      try {
        val = apiRef.current.getCellValue(
          prospectiveSuppliersRows.length,
          'name'
        );
      } catch (e) {
        // ignore; there will be exception when empty row is not there
      }

      if (val?.trim() !== '') {
        apiRef.current.setSortModel([{ field: 'id', sort: 'desc' }]);

        let obj = {
          id: prospectiveSuppliersRows.length,
          type: SPLR_TYPE_NON_CURRENT,
          number: psDefaultStatus,
          name: '',
          status: psDefaultStatus,
          aeRating: psDefaultStatus,
          comments: '',
          expanded: true
        };

        dispatch(addSupplier({ ...obj }));
      }
    }
  }

  function addCurrentSupplier() {
    if (addRowEnabled) {
      let val;

      try {
        val = apiRef.current.getCellValue(
          prospectiveSuppliersRows.length,
          'name'
        );
      } catch (e) {
        // ignore; there will be exception when empty row is not there
      }

      if (val?.trim() !== '') {
        apiRef.current.setSortModel([{ field: 'id', sort: 'desc' }]);

        let obj = {
          id: prospectiveSuppliersRows.length,
          type: SPLR_TYPE_CURRENT,
          number: psDefaultStatus,
          name: '',
          status: psDefaultStatus,
          riskRating: psDefaultStatus,
          comments: '',
          expanded: true
        };

        dispatch(addSupplier({ ...obj }));

        setTimeout(() => {
          apiRef.current.startCellEditMode({
            id: prospectiveSuppliersRows.length,
            field: 'name'
          });
        }, [25]);
      }
    }
  }

  const handleDeleteClick = (rowId) => () => {
    dispatch(deleteSupplier(rowId));
    setDirty();
  };

  function processRowUpdate(updatedRow, originalRow) {
    let rowData = updatedRow;

    if (updatedRow.type === SPLR_TYPE_CURRENT) {
      rowData = {
        ...originalRow,
        status: updatedRow.status,
        comments: updatedRow.comments
      };
    }

    if (prospectiveSuppliersRows.length !== updatedRow.id) {
      dispatch(editSupplier(rowData));
      setDirty();
    }
    return rowData;
  }

  const noRowsMsgOverlay = () =>
    noRowsMessage ? (
      <Box>
        <Alert variant="info">{noRowsMessage}</Alert>
      </Box>
    ) : (
      <></>
    );

  return (
    <div>
      {isEdit && (
        <Box sx={(theme) => ({ mt: theme.fuel6Tokens.JddcSpacingSmall })}>
          <Button
            variant="primary"
            onClick={addNonCurrentSupplier}
            style={{ marginRight: '1em' }}
          >
            + Add Non Current Supplier
          </Button>
          <Button variant="secondary" onClick={addCurrentSupplier}>
            + Add Current Supplier
          </Button>
          <GetSDIMasterList
            commonProps={commonProps}
            setSdiMasterList={setSdiMasterList}
          />
        </Box>
      )}
      <Grid container>
        <Box sx={{ mt: isEdit ? 4 : '' }} width={'100%'}>
          <DataGridPro
            autoHeight={true}
            apiRef={apiRef}
            density="compact"
            isCellEditable={(params) => {
              if (
                params.row.type === SPLR_TYPE_CURRENT &&
                (EDITABLE_CURRENT_PS_COLUMNS.includes(params.field) ||
                  (params.field === 'name' && params.row.name === ''))
              )
                return true;
              else
                return (
                  params.row.type === SPLR_TYPE_NON_CURRENT &&
                  EDITABLE_NON_CURRENT_PS_COLUMNS.includes(params.field)
                );
            }}
            initialState={{
              sorting: {
                sortModel: [{ field: 'id', sort: 'desc' }]
              },
              columns: {
                columnVisibilityModel: {
                  id: false
                }
              },
              pinnedColumns: { right: ['actions'] }
            }}
            onProcessRowUpdateError={handleProcessRowUpdateError}
            processRowUpdate={(updatedRow, originalRow) => {
              return processRowUpdate(
                updatedRow,
                originalRow,
                updateSupplierStatus,
                updateSupplierComments
              );
            }}
            slots={{
              detailPanelExpandIcon: () => <fuel-icon name="content_expand" />,
              detailPanelCollapseIcon: () => (
                <fuel-icon name="content_collapse" />
              ),
              noRowsOverlay: noRowsMsgOverlay
            }}
            getRowHeight={() => 'auto'}
            columns={columns}
            rows={prospectiveSuppliersRows}
            getDetailPanelHeight={getDetailPanelHeight}
            getDetailPanelContent={getDetailPanelContent}
            detailPanelExpandedRowIds={expandedRowIdSet}
            onDetailPanelExpandedRowIdsChange={
              handleDetailPanelExpandedRowIdsChange
            }
            onCellClick={handleCellClick}
            sx={supplierDataGridSx}
          />
        </Box>
      </Grid>
    </div>
  );
}
